import type { User } from '../../types';
import type { EngagementTask } from './types';
import { minimalTaskForContact } from './liveEngagement';

export type QuickEngagementFilterType = 'missing_availability' | 'birthday_today' | 'birthday_week';

export interface UserQuickEngagementButton {
  id: string;
  title: string;
  filterType: QuickEngagementFilterType;
  /** Placeholders: {{firstName}}, {{recruiterName}} */
  messageTemplate?: string;
  createdAt: string;
}

export const QUICK_FILTER_LABELS: Record<QuickEngagementFilterType, string> = {
  missing_availability: 'Geen (duidelijke) beschikbaarheid',
  birthday_today: 'Verjaardag vandaag',
  birthday_week: 'Verjaardag binnen 7 dagen',
};

export const SUGGESTED_QUICK_BUTTONS: Omit<UserQuickEngagementButton, 'id' | 'createdAt'>[] = [
  {
    title: 'Onbrekende beschikbaarheidsdatum',
    filterType: 'missing_availability',
    messageTemplate:
      'Hoi {{firstName}},\n\nIk mis nog je beschikbaarheid in ons systeem. Kun je kort aangeven vanaf wanneer je (eventueel) beschikbaar bent?\n\nGroet,\n{{recruiterName}}',
  },
  {
    title: 'Verjaardag — beschikbaarheid vragen',
    filterType: 'birthday_week',
    messageTemplate:
      'Hoi {{firstName}},\n\nVan harte gefeliciteerd met je verjaardag! 🎂 Dit is een mooi moment om even te vernemen: wat is je actuele beschikbaarheid?\n\nGroet,\n{{recruiterName}}',
  },
];

const storageKey = (userId: string) => `wt_engagement_quick_buttons_${userId}`;

export function loadQuickButtons(userId: string): UserQuickEngagementButton[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveQuickButtons(userId: string, buttons: UserQuickEngagementButton[]) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(buttons));
  } catch (e) {
    console.warn('[quickEngagementButtons] save failed', e);
  }
}

/** JSON velden op kandidaat: sommige omgevingen hebben geen kolom `candidates.data`, alleen `ai_parsed_data`. */
function candidateConsentBag(c: any): Record<string, unknown> {
  const ai =
    c?.ai_parsed_data && typeof c.ai_parsed_data === 'object' && !Array.isArray(c.ai_parsed_data)
      ? (c.ai_parsed_data as Record<string, unknown>)
      : {};
  const d =
    c?.data && typeof c.data === 'object' && !Array.isArray(c.data) ? (c.data as Record<string, unknown>) : {};
  return { ...ai, ...d };
}

export function hasCandidateEngagementConsent(c: any): boolean {
  const tags = (c?.tags as string[]) || [];
  const data = candidateConsentBag(c);
  const opted =
    tags.some((t) => /niet\s*benaderen|do not contact|opt-?out/i.test(t)) ||
    data?.do_not_contact === true ||
    data?.opt_out === true;
  if (opted) return false;
  if (data?.gdpr_consent === false || data?.consent_ok === false) return false;
  return true;
}

export function isCandidateMissingAvailability(c: any): boolean {
  const v = c?.availability;
  if (v == null) return true;
  const s = String(v).trim();
  if (!s) return true;
  if (/^(n\.a\.?|nvt|onbekend|unknown|geen idee|—|-|\.{3,}|todo|tbd)$/i.test(s)) return true;
  return false;
}

export function tryParseDateString(raw: string): Date | null {
  const t = raw.trim();
  if (!t) return null;
  const iso = new Date(t);
  if (!Number.isNaN(iso.getTime()) && /\d{4}/.test(t)) {
    return iso;
  }
  const m = t.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})/);
  if (m) {
    const a = parseInt(m[1], 10);
    const b = parseInt(m[2], 10);
    let y = parseInt(m[3], 10);
    if (y < 100) y += y > 50 ? 1900 : 2000;
    // NL often DD-MM-YYYY
    let day = a;
    let month = b - 1;
    if (a <= 12 && b > 12) {
      month = a - 1;
      day = b;
    }
    const d = new Date(y, month, day);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

/** Eerste bruikbare beschikbaarheidsdatum uit profiel (niet “onbekend”-tekst). */
export function parseCandidateAvailabilityDate(candidate: any): Date | null {
  const rawFields = [
    candidate?.data?.available_from,
    candidate?.available_from,
    candidate?.ai_parsed_data?.available_from,
    candidate?.availability,
  ];
  for (const v of rawFields) {
    if (v == null) continue;
    const s = String(v).trim();
    if (!s) continue;
    if (isCandidateMissingAvailability({ ...candidate, availability: s })) continue;
    const d = tryParseDateString(s);
    if (d && !Number.isNaN(d.getTime())) return d;
  }
  return null;
}

/**
 * Beschikbaarheid “loopt af” of moet ververst worden: datum ligt tussen X dagen geleden en Y dagen vooruit.
 */
export function isAvailabilityInExpiringWindow(candidate: any, pastDays = 14, futureDays = 60): boolean {
  if (!hasCandidateEngagementConsent(candidate)) return false;
  if (isCandidateMissingAvailability(candidate)) return false;
  const d = parseCandidateAvailabilityDate(candidate);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(d);
  end.setHours(0, 0, 0, 0);
  const diff = Math.round((end.getTime() - today.getTime()) / 86400000);
  return diff >= -pastDays && diff <= futureDays;
}

/** Beschikbaarheid valt binnen de komende N kalenderdagen (vandaag t/m +days). */
export function isAvailabilityDateWithinNextDays(candidate: any, days = 31): boolean {
  if (!hasCandidateEngagementConsent(candidate)) return false;
  if (isCandidateMissingAvailability(candidate)) return false;
  const d = parseCandidateAvailabilityDate(candidate);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(d);
  end.setHours(0, 0, 0, 0);
  const diff = Math.round((end.getTime() - today.getTime()) / 86400000);
  return diff >= 0 && diff <= days;
}

export function extractBirthDate(candidate: any): Date | null {
  const p = candidate?.ai_parsed_data || candidate?.parsed_cv;
  const candidates: unknown[] = [
    p?.personalInfo?.dateOfBirth,
    p?.personalInfo?.geboortedatum,
    p?.dateOfBirth,
    p?.geboortedatum,
    candidate?.data?.date_of_birth,
    candidate?.data?.geboortedatum,
  ];
  for (const c of candidates) {
    if (typeof c === 'string') {
      const d = tryParseDateString(c);
      if (d) return d;
    }
  }
  return null;
}

function isBirthdayToday(birth: Date): boolean {
  const n = new Date();
  return birth.getMonth() === n.getMonth() && birth.getDate() === n.getDate();
}

function getUpcomingBirthdayDate(birth: Date): Date {
  const now = new Date();
  const y = now.getFullYear();
  let next = new Date(y, birth.getMonth(), birth.getDate());
  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  next.setHours(0, 0, 0, 0);
  if (next < today0) {
    next = new Date(y + 1, birth.getMonth(), birth.getDate());
  }
  return next;
}

function isBirthdayWithinDays(birth: Date, maxDays: number): boolean {
  const upcoming = getUpcomingBirthdayDate(birth);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((upcoming.getTime() - today.getTime()) / 86400000);
  return diff >= 0 && diff <= maxDays;
}

function applyTemplate(tpl: string | undefined, c: any, recruiter: User): string | undefined {
  if (!tpl?.trim()) return undefined;
  const first = (c.name || '').split(/\s+/)[0] || '';
  return tpl.replace(/\{\{firstName\}\}/g, first).replace(/\{\{recruiterName\}\}/g, recruiter.name || '');
}

function defaultMessage(filter: QuickEngagementFilterType, c: any, user: User): string {
  const first = (c.name || '').split(/\s+/)[0] || 'daar';
  if (filter === 'missing_availability') {
    return `Hoi ${first},\n\nKun je je beschikbaarheid even laten weten? Dan kan ik je beter matchen.\n\nGroet,\n${user.name || ''}`;
  }
  if (filter === 'birthday_today') {
    return `Hoi ${first},\n\nVan harte gefeliciteerd! 🎂 Zou je kort willen delen wat je huidige beschikbaarheid is?\n\nGroet,\n${user.name || ''}`;
  }
  return `Hoi ${first},\n\nBijna jarig — alvast gefeliciteerd! 🎉 Mag ik je actuele beschikbaarheid weten?\n\nGroet,\n${user.name || ''}`;
}

export function buildTasksFromQuickButton(
  poolRows: any[],
  button: UserQuickEngagementButton,
  user: User
): EngagementTask[] {
  const unwrap = (row: any) => row?.candidate || row;
  const out: EngagementTask[] = [];

  for (const row of poolRows) {
    const c = unwrap(row);
    if (!c?.id || !hasCandidateEngagementConsent(c)) continue;

    let match = false;
    if (button.filterType === 'missing_availability') {
      match = isCandidateMissingAvailability(c);
    } else if (button.filterType === 'birthday_today') {
      const b = extractBirthDate(c);
      match = b ? isBirthdayToday(b) : false;
    } else {
      const b = extractBirthDate(c);
      match = b ? isBirthdayWithinDays(b, 7) : false;
    }

    if (!match) continue;

    const msg =
      applyTemplate(button.messageTemplate, c, user) || defaultMessage(button.filterType, c, user);
    const task = minimalTaskForContact(
      {
        id: c.id,
        name: c.name || 'Kandidaat',
        email: c.email,
        phone: c.phone,
        opted_out: false,
      },
      null,
      user,
      { reason: button.title, message: msg }
    );
    task.id = `quick-${button.id}-${c.id}`;
    task.task_type =
      button.filterType === 'missing_availability' ? 'availability_check' : 'availability_check';
    task.suggested_channel = 'whatsapp_personal';
    task.priority = button.filterType === 'birthday_today' ? 'high' : 'medium';
    out.push(task);
  }

  out.sort((a, b) => (a.candidate_name || '').localeCompare(b.candidate_name || '', 'nl'));
  return out;
}

export function newQuickButtonId(): string {
  return `qb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
